import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { NewApiBaseUrl } from '../consts/new-api-base-url'
import useHttpApiNew from '../hooks/useHttpApiNew'
import { shouldSkipOnlineApiCalls } from '../offline/offline-session-utils'
import { canUserAccessActionHistory } from '../pages/Statistics/statistics-report-type'
// eslint-disable-next-line no-restricted-globals
const logsBaseUrl = new URL('/api/action_log', location)
const newLogsBaseUrl = new URL(`${NewApiBaseUrl}/ActionLog`)

// Step 1: Create Context (default value is optional)
const ActionLogsContext = createContext()

// Step 2: Create Provider Component
export const ActionLogsProvider = ({ children }) => {
  const location = useLocation()
  const token = (() => {
    if (location.state?.token) return location.state.token
    try {
      const raw = localStorage.getItem('adams_user')
      const user = raw ? JSON.parse(raw) : null
      return user?.token || localStorage.getItem('token') || null
    } catch {
      return localStorage.getItem('token') || null
    }
  })()
  const init = (() => {
    if (location.state?.init) return location.state.init
    try {
      const raw = localStorage.getItem('adams_user')
      const user = raw ? JSON.parse(raw) : null
      return user?.init || null
    } catch {
      return null
    }
  })()
  const [logs, setLogs] = useState(null)
  const [count, setCount] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const { request } = useHttpApiNew()

  const fetchLogs = useCallback(async params => {
    try {
      const url = new URL(newLogsBaseUrl)
      url.searchParams.set('limit', 500)

      if (params && params.length) {
        params.forEach(([k, v]) => {
          url.searchParams.append(k, v)
        })
      }
      const responseActionLogs = await request(url, 'GET', null, {
        Authorization: `Bearer ${token}`
      })
      if (responseActionLogs.responseAnyError) return;
      setLogs(responseActionLogs.responseData)
      const count = responseActionLogs.responseData.length - responseActionLogs.responseData.map(log => log.status).reduce((a, b) => a + b, 0)
      setCount(count)
    } catch (error) {
      setLogs([])
      setCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [token, request])

  // Fetch initial count from API on mount (admin + org admin + multi-org admin).
  // Skip on /signup: no action log after registration flow (token may exist for Auth/Add only).
  useEffect(() => {
    if (location.pathname === '/signup') return
    if (shouldSkipOnlineApiCalls()) {
      setIsLoading(false)
      return
    }
    if (!canUserAccessActionHistory(init?.user)) {
      setIsLoading(false)
      return
    }
    const userType = init?.user?.type
    if (token && userType !== undefined && userType !== null && userType >= 0) fetchLogs()
  }, [token, init?.user, location.pathname, fetchLogs])

  // Update function (can be called from nested components)
  const setAsRead = async id => {
    const response = await request(newLogsBaseUrl, 'POST', id, {
      Authorization: `Bearer ${token}`
    })
    if (response.responseAnyError) return;
    setCount(count - 1)
    setLogs(old => old.map(log => log.id === id ? { ...log, status: 1 } : log))
  }

  return (
    <ActionLogsContext.Provider value={{ logs, count, setAsRead, isLoading, fetchLogs }}>
      {children}
    </ActionLogsContext.Provider>
  )
}

// Step 3: Custom Hook for easy access
export const useActionLogs = () => {
  const context = useContext(ActionLogsContext)
  if (!context) throw new Error('useCount must be used within a NewLogsProvider')
  return context // Returns { count, setCount }
}

